import { FC, SyntheticEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { OverlayTrigger, Tab, Tabs, Tooltip } from 'react-bootstrap'
import { useIntl } from 'react-intl'

import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DataTablePFSEvent } from 'primereact/datatable'

import { EquipmentTable } from 'pages/costs/EquipmentAndConstruction/EquipmentTable'
import { FOTable } from 'pages/costs/EquipmentAndConstruction/FOTable'
import {
	buildEmptyPagedResponse,
	getCountryIdsFromTableFilter,
	getSelectFilterValues,
	mergeCountryFilters,
	useCostsCountryCatalog,
} from 'pages/costs/common/useCostsCountryFilters'
import { withDownloadNotificationNames } from 'pages/costs/common/downloadNotificationIntl'
import { useColumnTextFilter } from 'pages/costs/common/tableTextFilter'
import { useTableSort } from 'pages/costs/common/useTableSort'
import { toast } from 'react-toastify'
import {
	HardFilterWrapper,
	HardFilterWrapperHandle,
	UfinetActionButtonHandle,
	UfinetBox,
	UfinetSectionBox,
} from 'ufinet-web-components'
import {
	AreaType,
	AuthContext,
	defaultPageNumber,
	defaultPageSize,
	EmptyPagingData,
	equipmentCostsService,
	foCostsService,
	getTableTranslations,
	ICostsEquipmentResponse,
	ICostsFOResponse,
	ICostsQueryCriteria,
	IUfinetSelectOption,
	mapFromPagedResponseToPagingData,
	PagedResponse,
	PagingData,
	useAsync,
} from 'ufinet-web-functions'
import { IXLSDownloadRequest } from 'ufinet-web-functions/dist/model/common/networking/typesAndValues'

enum TabsOptions {
	FO = 'FO',
	EQUIPMENT = 'EQUIPMENT',
}

export type EquipmentAndConstructionTableBaseProps = {
	findCosts: () => void
	className?: string
	loadingRecords?: boolean
	pagingData?: PagingData
	countryFilterOptions?: IUfinetSelectOption<string>[]
	onPage?: (event: DataTablePFSEvent) => void
	onSort?: (event: DataTablePFSEvent) => void
	onFilter?: (event: DataTablePFSEvent) => void
	onFilterClear?: () => void
	clientNameFilter?: ReturnType<typeof useColumnTextFilter>
	areaNameFilter?: ReturnType<typeof useColumnTextFilter>
	updatedByFilter?: ReturnType<typeof useColumnTextFilter>
}

type EquipmentOrConstructionCost = ICostsFOResponse | ICostsEquipmentResponse
type PagedEquipmentOrConstructionCost = PagedResponse<EquipmentOrConstructionCost>

const EquipmentAndConstructionPage: FC = () => {
	const intl = useIntl()
	const searchButtonRef = useRef<UfinetActionButtonHandle>()
	// Guards against an in-flight, slower search overwriting the results of a newer one (e.g. clicking
	// Buscar right after typing a filter): only the response of the latest request is applied.
	const requestIdRef = useRef(0)
	const [tab, setTab] = useState<TabsOptions>(TabsOptions.FO)
	const [foResponse, setFoResponse] = useState<ICostsFOResponse[]>([])
	const [equipmentResponse, setEquipmentResponse] = useState<ICostsEquipmentResponse[]>([])
	const foSort = useTableSort<ICostsFOResponse>()
	const equipmentSort = useTableSort<ICostsEquipmentResponse>()
	const [foPagingData, setFoPagingData] = useState<PagingData>(EmptyPagingData)
	const [equipmentPagingData, setEquipmentPagingData] = useState<PagingData>(EmptyPagingData)
	const [pagingState, setPagingState] = useState({ pageNumber: defaultPageNumber, pageSize: defaultPageSize })
	const [tableCountryFilterIds, setTableCountryFilterIds] = useState<string[]>([])
	const [tableZoneTypeFilterIds, setTableZoneTypeFilterIds] = useState<string[]>([])
	const [tableInfrastructureTypeFilterIds, setTableInfrastructureTypeFilterIds] = useState<string[]>([])
	const [tableServiceTypeFilterIds, setTableServiceTypeFilterIds] = useState<string[]>([])
	const [tableTechnicalAttributeFilterIds, setTableTechnicalAttributeFilterIds] = useState<string[]>([])
	const [tableTransmissionMediumFilterIds, setTableTransmissionMediumFilterIds] = useState<string[]>([])

	const [loadingRecords, setLoadingRecords] = useState<boolean>(true)

	const authData = useContext(AuthContext)
	const { countryFilterOptions } = useCostsCountryCatalog(authData)

	const _FOCostsService = useMemo(() => foCostsService(authData, intl), [authData, intl])
	const _equipmentCostsService = useMemo(() => equipmentCostsService(authData, intl), [authData, intl])

	// Free-text Cliente/Área filters: see tableTextFilter.tsx for why (broken built-in select match mode).
	// Kept separate per tab so switching tabs doesn't clear the other tab's search term.
	const foClientNameFilter = useColumnTextFilter('COST.CLIENT')
	const foAreaNameFilter = useColumnTextFilter('COST.AREA')
	const foUpdatedByFilter = useColumnTextFilter('COST.UPDATEDBY')
	const equipmentClientNameFilter = useColumnTextFilter('COST.CLIENT')
	const equipmentAreaNameFilter = useColumnTextFilter('COST.AREA')
	const equipmentUpdatedByFilter = useColumnTextFilter('COST.UPDATEDBY')

	const upperFilterRef = useRef<HardFilterWrapperHandle>(null)

	// Reset to page 1 whenever the active tab's free-text Cliente/Área search term changes, so results
	// from the new search aren't hidden behind a page number that no longer makes sense.
	useEffect(() => {
		setPagingState((prev) => ({ ...prev, pageNumber: defaultPageNumber }))
	}, [
		foClientNameFilter.value,
		foAreaNameFilter.value,
		foUpdatedByFilter.value,
		equipmentClientNameFilter.value,
		equipmentAreaNameFilter.value,
		equipmentUpdatedByFilter.value,
	])

	const findCosts = useCallback((): Promise<PagedEquipmentOrConstructionCost | null> => {
		const requestId = ++requestIdRef.current
		setLoadingRecords(true)
		searchButtonRef.current?.changeActionStatus(true)
		const hardFilterCountryIds = upperFilterRef.current?.getCountries().map((e) => e.value) || []
		const { countries, hasImpossibleCountryFilter } = mergeCountryFilters(hardFilterCountryIds, tableCountryFilterIds)
		if (hasImpossibleCountryFilter) {
			return Promise.resolve(
				buildEmptyPagedResponse<EquipmentOrConstructionCost>(pagingState.pageNumber, pagingState.pageSize)
			)
		}
		// Cliente/Área are searched server-side (LIKE '%term%', see aqs-backend's
		// clientNameSearch/networkAreaNameSearch) so results cover the whole dataset, not just this page.
		const isEquipmentTab = tab === TabsOptions.EQUIPMENT
		const clientNameFilter = isEquipmentTab ? equipmentClientNameFilter : foClientNameFilter
		const areaNameFilter = isEquipmentTab ? equipmentAreaNameFilter : foAreaNameFilter
		const criteria: ICostsQueryCriteria & {
			pageNumber: number
			pageSize: number
			clientNameSearch?: string
			networkAreaNameSearch?: string
			updatedBy?: string
			zoneTypes?: string[]
			infrastructureTypes?: string[]
			serviceTypes?: string[]
			technicalAttributes?: string[]
			transmissionMediums?: string[]
		} = {
			countries,
			networkAreas: upperFilterRef.current?.getAreas().map((e) => e.value) || [],
			pageNumber: pagingState.pageNumber,
			pageSize: pagingState.pageSize,
			clientNameSearch: clientNameFilter.value,
			networkAreaNameSearch: areaNameFilter.value,
			// Actualizado Por: supported by both the FO (fiber construction) and Equipment endpoints.
			updatedBy: isEquipmentTab ? equipmentUpdatedByFilter.value : foUpdatedByFilter.value,
			zoneTypes: tableZoneTypeFilterIds,
			// Only relevant for FO (fiber optics); the Equipment DTO has no infrastructureType field and ignores it.
			infrastructureTypes: tableInfrastructureTypeFilterIds,
			// Only relevant for Equipment; the FO DTO ignores these.
			serviceTypes: tableServiceTypeFilterIds,
			technicalAttributes: tableTechnicalAttributeFilterIds,
			transmissionMediums: tableTransmissionMediumFilterIds,
		}
		const request = (tab === TabsOptions.FO
			? _FOCostsService.findByCriteria(criteria)
			: _equipmentCostsService.findByCriteria(criteria)) as unknown as Promise<PagedEquipmentOrConstructionCost>
		return request.then((result) => (requestId === requestIdRef.current ? result : null))
	}, [
		tab,
		_FOCostsService,
		_equipmentCostsService,
		pagingState.pageNumber,
		pagingState.pageSize,
		tableCountryFilterIds,
		tableZoneTypeFilterIds,
		tableInfrastructureTypeFilterIds,
		tableServiceTypeFilterIds,
		tableTechnicalAttributeFilterIds,
		tableTransmissionMediumFilterIds,
		foClientNameFilter.value,
		foAreaNameFilter.value,
		foUpdatedByFilter.value,
		equipmentClientNameFilter.value,
		equipmentAreaNameFilter.value,
		equipmentUpdatedByFilter.value,
	])

	const getParsedFOResult = (result: ICostsFOResponse[]) =>
		result.map((cost: ICostsFOResponse) => ({
			...cost,
			networkAreaName: getTableTranslations(cost.networkAreaName, intl),
			infrastructureType: intl.formatMessage({ id: cost.infrastructureType }),
			zoneType: intl.formatMessage({ id: cost.zoneType }),
		}))

	const getParsedEquipmentResult = (result: ICostsEquipmentResponse[]) =>
		result.map((cost: ICostsEquipmentResponse) => ({
			...cost,
			networkAreaName: getTableTranslations(cost.networkAreaName, intl),
			zoneType: intl.formatMessage({ id: cost.zoneType }),
		}))

	const onCostsFetched = (response: PagedEquipmentOrConstructionCost | null): void => {
		if (!response) return // superseded by a newer search; ignore this stale response
		const isLegacy = Array.isArray(response)
		const data = isLegacy ? (response as unknown as EquipmentOrConstructionCost[]) : response.data
		const newPagingData = isLegacy ? EmptyPagingData : mapFromPagedResponseToPagingData(response)
		if (tab === TabsOptions.FO) {
			setFoPagingData(newPagingData)
			setFoResponse(getParsedFOResult(data as ICostsFOResponse[]))
		} else {
			setEquipmentPagingData(newPagingData)
			setEquipmentResponse(getParsedEquipmentResult(data as ICostsEquipmentResponse[]))
		}
	}

	const onCostsFailedToFetch = (): void => {
		toast.error(intl.formatMessage({ id: 'COSTS.FETCH.ERROR' }))
	}

	const runFinally = () => {
		searchButtonRef.current?.changeActionStatus(false)
		setLoadingRecords(false)
	}

	// Collapse every filter/paging input into one string signature and drive the fetch off a single
	// incrementing token, so one user action = exactly one /find call. useAsync's internal effect compares
	// its deps per-element; passing the raw filter arrays made it re-fire on every new array reference
	// (6+ calls per change). A primitive token gated by the signature ignores that reference churn.
	const [searchToken, setSearchToken] = useState(0)
	const criteriaSignature = useMemo(
		() =>
			JSON.stringify([
				tab,
				pagingState.pageNumber,
				pagingState.pageSize,
				tableCountryFilterIds,
				tableZoneTypeFilterIds,
				tableInfrastructureTypeFilterIds,
				tableServiceTypeFilterIds,
				tableTechnicalAttributeFilterIds,
				tableTransmissionMediumFilterIds,
				foClientNameFilter.value,
				foAreaNameFilter.value,
				foUpdatedByFilter.value,
				equipmentClientNameFilter.value,
				equipmentAreaNameFilter.value,
				equipmentUpdatedByFilter.value,
			]),
		[
			tab,
			pagingState.pageNumber,
			pagingState.pageSize,
			tableCountryFilterIds,
			tableZoneTypeFilterIds,
			tableInfrastructureTypeFilterIds,
			tableServiceTypeFilterIds,
			tableTechnicalAttributeFilterIds,
			tableTransmissionMediumFilterIds,
			foClientNameFilter.value,
			foAreaNameFilter.value,
			foUpdatedByFilter.value,
			equipmentClientNameFilter.value,
			equipmentAreaNameFilter.value,
			equipmentUpdatedByFilter.value,
		]
	)
	useEffect(() => {
		setSearchToken((token) => token + 1)
	}, [criteriaSignature])

	const fetchAndStore = useAsync(
		{
			asyncFn: findCosts,
			onSuccess: onCostsFetched,
			onFailure: onCostsFailedToFetch,
			runFinally: runFinally,
			// skipMount: the initial load is triggered once by the signature effect above (token 0 -> 1),
			// so we avoid a duplicate fetch on first render.
			options: { skipMount: true },
		},
		[searchToken]
	)

	const handlePage = useCallback((event: DataTablePFSEvent) => {
		setPagingState({ pageNumber: event.page ?? defaultPageNumber, pageSize: event.rows ?? defaultPageSize })
	}, [])

	const handleFilter = useCallback((event: DataTablePFSEvent) => {
		const countryIds = getCountryIdsFromTableFilter(event)
		const zoneTypeIds = getSelectFilterValues(event, 'zoneType')
		const infrastructureTypeIds = getSelectFilterValues(event, 'infrastructureType')
		const serviceTypeIds = getSelectFilterValues(event, 'serviceTypeName')
		const technicalAttributeIds = getSelectFilterValues(event, 'technicalAttributeName')
		const transmissionMediumIds = getSelectFilterValues(event, 'transmissionMediumName')
		setPagingState((prev) => ({ ...prev, pageNumber: defaultPageNumber }))
		setTableCountryFilterIds(countryIds)
		setTableZoneTypeFilterIds(zoneTypeIds)
		setTableInfrastructureTypeFilterIds(infrastructureTypeIds)
		setTableServiceTypeFilterIds(serviceTypeIds)
		setTableTechnicalAttributeFilterIds(technicalAttributeIds)
		setTableTransmissionMediumFilterIds(transmissionMediumIds)
	}, [])

	const handleDownloadExcel = () => {
		const hardFilterCountryIds = upperFilterRef.current?.getCountries().map((e) => e.value) || []
		const { countries, hasImpossibleCountryFilter } = mergeCountryFilters(hardFilterCountryIds, tableCountryFilterIds)
		if (hasImpossibleCountryFilter) return
		const areaOptions = upperFilterRef.current?.getAreas() || []
		const data: IXLSDownloadRequest = {
			countries,
			networkAreas: areaOptions.map((e) => e.value),
		}
		const idToName = new Map([
			...(countryFilterOptions?.map((o): [string, string] => [o.value, o.label]) || []),
			...areaOptions.map((o): [string, string] => [o.value, o.label]),
		])
		const notificationIntl = withDownloadNotificationNames(intl, idToName)
		tab === TabsOptions.FO
			? foCostsService(authData, notificationIntl).downloadXLS(data)
			: equipmentCostsService(authData, notificationIntl).downloadXLS(data)
	}

	/// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
	/// /////////////////////////////////////// HEADER TABLE AND ACTION BUTTONS //////////////////////////////////////////
	/// //////////////////////////////////////////////////////////////////////////////////////////////////////////////////

	const getActionButtons = () => {
		return (
			<>
				<OverlayTrigger
					placement="bottom"
					delay={{ show: 250, hide: 400 }}
					overlay={<Tooltip className="tooltip-dark">{intl.formatMessage({ id: 'DOWNLOAD_EXCEL' })}</Tooltip>}
				>
					<div className="d-inline">
						<FontAwesomeIcon className="fa-icon-primary" size="1x" icon={faDownload} onClick={handleDownloadExcel} />
					</div>
				</OverlayTrigger>
			</>
		)
	}

	const handleSelect = (eventKey: string | null, _e: SyntheticEvent<unknown>) => {
		setPagingState({ pageNumber: defaultPageNumber, pageSize: defaultPageSize })
		setTableCountryFilterIds([])
		eventKey === 'fiber-cost' ? setTab(TabsOptions.FO) : setTab(TabsOptions.EQUIPMENT)
	}

	return (
		<UfinetBox limitHeightToPageBottom={false}>
			<HardFilterWrapper
				ref={upperFilterRef}
				actionButtonRef={searchButtonRef}
				forceUpdate={tab}
				search={fetchAndStore}
				networkAreaType={AreaType.OPTICAL_FIBER_AREA}
				actionButtons={getActionButtons()}
			/>
			<UfinetSectionBox>
				<Tabs id="equipment-construction-tabs" onSelect={handleSelect} defaultActiveKey="fiber-cost">
					<Tab eventKey="fiber-cost" title={intl.formatMessage({ id: 'TABS.EQUIPMENT&CONSTRUCTION.FIBER_COST' })}>
						<FOTable
							className="fs-5"
							content={foSort.sortData(foResponse)}
							countryFilterOptions={countryFilterOptions}
							findCosts={fetchAndStore}
							loadingRecords={loadingRecords}
							pagingData={foPagingData}
							onPage={handlePage}
							onSort={foSort.handleSort}
							onFilter={handleFilter}
							clientNameFilter={foClientNameFilter}
							areaNameFilter={foAreaNameFilter}
							updatedByFilter={foUpdatedByFilter}
						/>
					</Tab>
					<Tab
						eventKey="equipment-cost"
						title={intl.formatMessage({ id: 'TABS.EQUIPMENT&CONSTRUCTION.EQUIPMENT_COST' })}
					>
						<EquipmentTable
							className="fs-5"
							content={equipmentSort.sortData(equipmentResponse)}
							countryFilterOptions={countryFilterOptions}
							findCosts={fetchAndStore}
							loadingRecords={loadingRecords}
							pagingData={equipmentPagingData}
							onPage={handlePage}
							onSort={equipmentSort.handleSort}
							onFilter={handleFilter}
							clientNameFilter={equipmentClientNameFilter}
							areaNameFilter={equipmentAreaNameFilter}
							updatedByFilter={equipmentUpdatedByFilter}
						/>
					</Tab>
				</Tabs>
			</UfinetSectionBox>
		</UfinetBox>
	)
}
export { EquipmentAndConstructionPage }
